import { observer } from 'mobx-react-lite'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { NoteCardContainer } from '@/features/jobs/details/components/JobSections/NoteCard/NoteCardContainer'
import { TxfButtonContainer } from '@/features/jobs/details/components/TxfButton/TxfButtonContainer'
import {
  ClientChecklist,
  OverviewPane,
  RecentNotes,
  RecentUploads,
  UploadRow,
} from '@/features/jobs/details/components/JobSections/OverviewPane'

export interface OverviewPaneContainerProps {
  vm: JobDetailsViewModel
}

export const OverviewPaneContainer = observer(function OverviewPaneContainer({
  vm,
}: OverviewPaneContainerProps) {
  return (
    <OverviewPane>
      {vm.canViewDocs && <ClientChecklistContainer vm={vm} />}
      {vm.canViewDocs && <RecentUploadsContainer vm={vm} />}
      {vm.canViewJobNotes && <RecentNotesContainer vm={vm} />}
    </OverviewPane>
  )
})

const ClientChecklistContainer = observer(function ClientChecklistContainer({
  vm,
}: OverviewPaneContainerProps) {
  return (
    <ClientChecklist
      rows={vm.overviewVm.checklistRows}
      isSkeleton={vm.overviewVm.isChecklistFetchingInitial}
    />
  )
})

const RecentUploadsContainer = observer(function RecentUploadsContainer({
  vm,
}: OverviewPaneContainerProps) {
  return (
    <RecentUploads
      documentCount={vm.documentsVm.documentCount}
      hasDocuments={vm.overviewVm.hasDocuments}
      isSkeleton={vm.documentsVm.isFetchingInitial}
      isEmpty={!vm.overviewVm.hasRecentDocuments}
      onViewAll={vm.viewDocumentsSection}
      txfButton={
        vm.documentsVm.txfJobVm.canView && (
          <TxfButtonContainer vm={vm.documentsVm.txfJobVm} />
        )
      }
    >
      {vm.overviewVm.recentDocumentVms.map((itemVm) => (
        <UploadRow
          key={itemVm.document.id}
          title={itemVm.document.title}
          isProcessing={itemVm.document.isProcessing}
          documentTypeName={itemVm.document.documentType?.name}
          ownerName={itemVm.document.owner.displayName}
          dateCreated={itemVm.document.dateCreated}
          onPress={itemVm.view}
          actions={
            itemVm.document.downloadable
              ? {
                  onView: itemVm.preview,
                  onDownload: itemVm.download,
                  onUnlink: itemVm.canUnlink ? itemVm.unlink : undefined,
                  onDelete: itemVm.canDelete ? itemVm.delete : undefined,
                }
              : null
          }
        />
      ))}
    </RecentUploads>
  )
})

const RecentNotesContainer = observer(function RecentNotesContainer({
  vm,
}: OverviewPaneContainerProps) {
  const notesVm = vm.jobNotesVm

  return (
    <RecentNotes
      canWrite={notesVm.canWrite}
      onAddNote={vm.composeNote}
      noteCount={notesVm.notes.length}
      isSkeleton={notesVm.isFetchingInitial}
      countLabel={notesVm.countLabel}
      onViewAll={vm.viewNotesSection}
    >
      {notesVm.notes.map((note) => (
        <NoteCardContainer key={note.id} vm={notesVm} noteVm={note} />
      ))}
    </RecentNotes>
  )
})
